import _ from 'lodash';
import Debug from '@/util/debug';
import { STATUS_FLOW_ASAP } from '@/services/StatusFlowService';
import config, { setConfig } from '@/configuration/infobox';
import { GOAL_POST_ENGAGEMENT } from '@/services/GoalTypeService';

const debug = Debug('full-info-box');

// dataFn: (campaign, translate, fullInfoBox) => String
const createField = (name, caption, dataFn) => ({
  name,
  caption,
  data: dataFn,
});

const availableFields = [
  createField('campaign.goal', 'draft_field.title.campaign-goal',
    (campaign, translator) => translator(`terms.campaign-goal.${campaign.goal_type}`),
  ),
  createField('target.locations', 'draft_field.title.locations',
    (c, t) => c.target.locations
      .map(l => t('draft_field.format.location', { name: l.name, type: l.type }))
      .join(', '),
  ),
  createField('target.locations_with_radius', 'draft_field.title.locations',
    (c, t) => c.target.locations
      .map(l => (c.target.radius > 0 ?
      t('draft_field.format.location_with_radius', { name: l.name, type: l.type, radius: c.target.radius, unit: c.target.distance_unit })
      : t('draft_field.format.location', { name: l.name, type: l.type })))
      .join(', '),
  ),
  createField('target.radius', 'draft_field.title.radius',
    (c, t) => t('draft_field.format.radius', { radius: c.target.radius, unit: c.target.distance_unit }),
  ),
  createField('target.gender', 'draft_field.title.gender',
    (c, t) => t('draft_field.format.gender', { genders: c.target.genders }),
  ),
  createField('target.age', 'draft_field.title.age',
    (c, t) => t('draft_field.format.age', { from: c.target.ages.from, to: c.target.ages.to }),
  ),
  createField('target.interest', 'draft_field.title.interest',
    (c, t, box) => {
      const interests = box.interests
        .filter(i => c.target.interests.indexOf(i.id) !== -1)
        .map(i => {
          const key = `terms.interests.${i.slug}.name`;
          if (key === t(key)) {
            return i.name;
          }
          return t(key);
        })
        .join(', ');

      if (interests.length) {
        return interests;
      }
      return t('pos.target.interest.modal.no-interest');
    },
  ),
  createField('target.language', 'draft_field.title.language',
    (c, t) => t('draft_field.format.language', { type: c.target.language }),
  ),
  createField(
    'target.placement',
    'draft_field.title.placement',
    (c, t) => {
      const useInstagram = _.get(c, 'target.use_instagram', false);
      return t(`terms.placement.${useInstagram ? 'facebook-instagram' : 'facebook'}`);
    },
  ),
  createField('budget', 'draft_field.title.budget',
    (c, t, box) => t('{{ price, currency }} $t(pos.offer.context.{{ context }}.price)', {
      price: box.package.budget.budget,
      context: c.mode,
    }),
  ),
  createField('mode', 'draft_field.title.mode', (c, t) => t('draft_field.format.mode', { mode: c.mode })),
  createField('duration', 'draft_field.title.duration', (c, t) => t('terms.mode.multi_month', { duration: _.get(c, 'details.duration') })),
  createField('reach.guaranteed', 'draft_field.title.guaranteed-reach',
    (c, t, box) => t('draft_field.format.target', { users: box.package.reach.min }),
  ),
  createField('format', 'draft_field.title.format',
    (c, t) => {
      let type = t('draft_field.format.format', { format: c.type });
      if (
        Object.prototype.hasOwnProperty.call(c.details, 'extraCostFormat')
        && Number(c.details.extraCostFormat)
      ) {
        type += ` ${t('draft_field.format.format-extracost', { cost: c.details.extraCostFormat })}`;
      }
      return type;
    },
  ),
  createField('customer.company', 'draft_field.title.company', c => c.customer.name),
  createField('customer.id', 'draft_field.title.customer-remote-id', c => c.customer.remote_id),
  createField('customer.email', 'draft_field.title.customer-email', c => c.customer.email),
  createField('customer.phone', 'draft_field.title.phone', c => c.customer.phone),
  createField('lead-email', 'draft_field.title.lead-email', c => c.details.leadEmail),
  createField('lead-phone', 'draft_field.title.lead-phone', c => c.details.leadPhone),
  createField('privacy-policy-link', 'draft_field.title.privacy-policy-link', c => c.details.leadPrivacyLink),
  createField('customer.facebook_email', 'draft_field.title.facebook-email', c => c.facebook.email),
  createField('landing_page', 'draft_field.title.landing-page',
    (c, t) => t('draft_field.format.landing-page', {
      type: c.landing_page.type,
      url: c.landing_page.url,
    }),
  ),
  createField('facebook_page', 'draft_field.title.facebook-page',
    (c, t) => {
      if (c.facebook.page) {
        return c.facebook.page.name;
      } else if (!c.facebook.hasFacebookPage) {
        const mode = c.facebook.creationMode ? c.facebook.creationMode : 'no-page';
        let type = t('draft_field.format.facebook-page-mode', { mode });

        if (c.details.extraCostFacebookPage > 0) {
          type += ` ${t('draft_field.format.facebook-page-extracost', { cost: c.details.extraCostFacebookPage })}`;
        }
        return type;
      }
      return null;
    },
  ),
  createField('name', 'draft_field.title.name', c => c.name),
  createField('objective', 'draft_field.title.objective', (c, translator) => translator(`terms.objective-texts.${c.objective}.name`)),
  createField('brief', 'draft_field.title.brief', c => c.brief),
  createField('images', 'draft_field.title.images', (c, t) => t(`terms.misc.${c.images.length ? 'yes' : 'no'}`)),
  createField('start_date', 'draft_field.title.start-date',
    (c, t) => (c.flow === STATUS_FLOW_ASAP && !_.has(c, 'details.clone-of')
      ? STATUS_FLOW_ASAP
      : t('draft_field.format.start-date', { date: c.start_date })),
  ),
  createField('estimated_clicks', 'draft_field.title.estimated-clicks',
    (c, t, box) => {
      const clickRes = { min: box.package.clicks.min, max: box.package.clicks.max };
      const multi = parseFloat(_.get(box, 'businessCategory.clicks_multiplier', 0));
      if (!isNaN(multi) && multi > 0) {
        clickRes.min *= multi;
        clickRes.max *= multi;
      }
      return t('draft_field.format.clicks', clickRes);
    },
  ),
  createField('coupon_code', 'draft_field.title.coupon-code', c => c.details.alliedCoupon),
  createField('estimated_leads', 'draft_field.title.estimated-leads', (campaign, translator, box) => {
    let value = '';

    if (box.package.leads.max > 0) {
      value = `${box.package.leads.min} - ${box.package.leads.max} ${translator('leads')}`;
    }

    return value;
  }),
  createField('campaign.details.form_question', 'draft_field.title.form-question', c => c.details.form_question),
  createField(
    'creative_refresh_count',
    'draft_field.title.number-of-refresh',
    c => _.get(c.details, 'creativeRefreshCount', null),
  ),
];

const filterFields = {
  'target.radius': function filter() {
    if (this.locationType === 'address') {
      return true;
    }

    if (this.locationType === 'zip' || this.locationType === 'region') {
      return false;
    }

    if (this.campaign.target.locations.length > 1) {
      return false;
    }
    return true;
  },
  duration: function filter() {
    return !!_.get(this, 'campaign.details.duration');
  },
  'campaign.details.form_question': function filter() {
    if (this.campaign.details.form_question &&
      this.campaign.details.form_question.trim().length > 0
    ) {
      return true;
    }
    return false;
  },
  estimated_leads: function filter() {
    if (_.get(this, 'package.leads.max', 0) > 0) {
      return true;
    }
    return false;
  },
  landing_page: function filter() {
    if (_.get(this, 'campaign.goal_type', 0) === GOAL_POST_ENGAGEMENT) {
      return false;
    }
    return true;
  },
};

export default {
  dependencies: [
    'adApiService',
    'interestsService',
    'packageService',
    'genericConfigService',
    'clusterService',
  ],
  props: {
    title: String,
    campaign: Object,
    configuration: String,
    card: Boolean,
    showMore: {
      default: true,
      type: Boolean,
    },
  },
  data() {
    return {
      loading: true,
      error: null,
      interests: null,
      package: null,
      active: false,
      fields: [],
      businessCategory: null,
    };
  },
  mounted() {
    const fin = () => {
      debug.info('finished loading. error: %o', this.error);
      this.loading = false;
    };

    if (this.campaign.business_category_id) {
      this.adApiService.getBusinessCategory(this.campaign.business_category_id)
        .then(res => {
          this.businessCategory = res;
        });
    }
    Promise.all([
      this.interestsService.getInterests()
        .then(i => this.interests = i),
      this.packageService.getCampaignPackage(this.campaign, true)
        .then(p => this.package = p),
      Promise.all([
        this.genericConfigService.getConfig(),
        this.clusterService.getClusters(),
      ]).then(([cfg, clusters]) => {
        setConfig(cfg);
        debug.info('available clusters', clusters);
        const availableClusters = this.clusterService.availableClusters(this.campaign);
        const modeCount = _.uniqBy(availableClusters, 'mode').length;
        this.fields = _.get(config(this.campaign, modeCount), this.configuration, []);
      }),
    ])
    .then(fin)
    .catch(e => { this.error = e; fin(); });
  },
  computed: {
    showFields() {
      return this.fields.map(block => block
        .map(name => availableFields.find(f => f.name === name))
        .filter(f => !!f)
        .filter((f) => (typeof filterFields[f.name] === 'undefined' || filterFields[f.name].bind(this)()))
        .map(f => ({
          name: this.$t(f.caption),
          value: f.data(this.campaign, this.$t.bind(this), this),
        })),
      );
    },
    nameFieldClasses() {
      return this.card ? ['col-5'] : ['col-6', 'col-md-4'];
    },
    valueFieldClasses() {
      return this.card ? ['col-7'] : ['col-6', 'col-md-8'];
    },
    locationType() {
      return this.campaign.target.locations[0].type;
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/full-info-box/FullInfoBox.js